'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import {
  BottomSheetHandleArea,
  BottomSheetTitle,
  BottomSheetTitleBox,
  BottomSheetTitleContainer,
  Button,
  Dim,
  IcDelete,
  IconButton,
  Image,
  Sheet,
  SheetContent,
  SheetDimMotion,
  SheetMotion,
  SheetPortal,
  SheetTrigger,
  TextField,
  Typography,
} from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type { SearchProductService } from '@musinsa/services-curator';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { getBrandConfig } from '../../common/brandConfig';
import { useCuratorSnackbar, usePreventBodyScroll } from '../../common/hooks';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { useCreateProductLink } from '../hooks';

interface LinkGenerationBottomSheetProps {
  children: React.ReactNode;
  item: SearchProductService;
  onClickGenerateLink: () => void;
  /** 무신사 GTM/Amplitude `search_goods` 인덱스 (0-based) */
  productIndex?: number;
}

export const LinkGenerationBottomSheet = ({
  children,
  item,
  onClickGenerateLink,
  productIndex = 0,
}: LinkGenerationBottomSheetProps) => {
  const [isOpen, setIsOpen] = useState(false);

  usePreventBodyScroll({ open: isOpen });

  return (
    <Sheet direction="bottom" defaultOpen={false} open={isOpen}>
      <SheetTrigger
        asChild
        onClick={(e) => {
          onClickGenerateLink();
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        {children}
      </SheetTrigger>
      <SheetPortal>
        <SheetContent onPointerDownOutside={() => setIsOpen(false)}>
          <SheetMotion direction="bottom">
            <BottomSheetTitle>
              <BottomSheetHandleArea className="py-[6px]!" />
              <BottomSheetTitleContainer className="py-[9px]!">
                <BottomSheetTitleBox>
                  <Typography variant="title16pxMed">링크 생성</Typography>
                </BottomSheetTitleBox>
                <IconButton
                  size="size28x28"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                  }}
                >
                  <IcDelete />
                </IconButton>
              </BottomSheetTitleContainer>
            </BottomSheetTitle>

            <LinkGenerationBottomSheetContent item={item} productIndex={productIndex} />
          </SheetMotion>
        </SheetContent>
        <SheetDimMotion>
          <Dim />
        </SheetDimMotion>
      </SheetPortal>
    </Sheet>
  );
};

const LinkGenerationBottomSheetContent = ({
  item,
  className,
  productIndex,
}: {
  item: SearchProductService;
  className?: string;
  productIndex: number;
}) => {
  const { mutate: generateLinkMutation, isPending, data } = useCreateProductLink();
  const { service } = useDeviceInfo();
  const { serviceName } = getBrandConfig(service);
  const snackbar = useCuratorSnackbar();
  const generateLink = () => {
    generateLinkMutation({
      productId: String(item.goodsNo),
    });
  };

  useEffect(() => {
    generateLink();
  }, []);

  const handleCopyLink = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_product_link',
          section_name: 'LinkGeneration',
          section_title: 'LinkGeneration',
          position: -1,
          button_id: 'share_link',
          button_name: '링크 복사하기',
          item_no_list: item.goodsNo,
        },
      });
    }

    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'curatorlink_copy',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: String(item.goodsNo),
        index: productIndex,
        name: 'goods',
        section_name: 'search_goods',
        section_title: item.brandName,
      });
      pushDataLayer('click_button', {
        brand_id: GTM_NOTSET_STRING,
        button_id: 'curatorlink_copy',
        button_name: 'goods',
        category_id: String(item.goodsNo),
        index: `${productIndex}`,
        section_index: '1',
        section_name: 'search_goods',
      });
    }

    e.stopPropagation();

    if (!data?.link) return;

    navigator.clipboard.writeText(data.link);
    snackbar.show({
      message: '링크를 복사했습니다.',
    });
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('ko-KR').format(price);
  // RADAR 추가할인 반영된 최종가/할인율 우선 노출, 응답 부재 시 일반가/할인율로 fallback
  const displayPrice = item.finalPrice ?? item.price;
  const displayDiscount = item.finalDiscount ?? item.salesRate ?? 0;

  return (
    <div
      className={twMerge('flex flex-col px-4 bg-neutral-color-white safe-area-bottom', className)}
    >
      <div className="flex pt-[4px]! pb-3">
        <div className="w-[72px] h-[86px] shrink-0 rounded overflow-hidden">
          <Image
            src={`${item.imageUrl}?width=144&q=75&format=webp`}
            size="size72x86"
            alt={item.goodsName}
            style={{
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </div>
        <div className="w-[calc(100%-84px)] justify-center flex flex-col gap-[2px] px-3">
          <Typography variant="body13pxSemi" colorScheme="black">
            {item.brandName}
          </Typography>
          <Typography variant="body13pxReg" colorScheme="black">
            {item.goodsName}
          </Typography>
          <div className="flex gap-[2px]">
            <Typography variant="body13pxSemi" colorScheme="black">
              {displayDiscount ? <span className="text-red">{displayDiscount}%&nbsp;</span> : ''}
              {formatPrice(displayPrice)}원
            </Typography>
          </div>
        </div>
      </div>
      <TextField
        value={data?.link || ''}
        fullWidth
        readOnly
        placeholder="링크가 여기에 표시됩니다"
      />

      <Button className="my-3" size="36" onClick={handleCopyLink} disabled={!data || isPending}>
        <Typography variant="body13pxMed">링크 복사하기</Typography>
      </Button>

      <div className="flex flex-col gap pb-[12px]!">
        <Typography variant="body13pxReg" className="py-1" colorScheme="gray-600">
          콘텐츠 등록 시, 아래 내용을 표기해야 합니다.
        </Typography>
        <Typography variant="body13pxReg" className="py-1" colorScheme="gray-600">
          큐레이터 서비스는 {serviceName} 큐레이터 활동의 일환으로 구매가 발생할 시 일정 수수료를
          제공 받습니다.
        </Typography>
      </div>
    </div>
  );
};
